import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { ArrowDownward, ArrowUpward } from '@material-ui/icons';
import { lightTheme as theme } from '../../theme';

// Reusing primary color for header as requested ("sober" color from breadcrumb)
const HEADER_BG = theme.colors.primary;

const TableContainer = styled.div`
    width: 100%;
    overflow: hidden;
    border-radius: 12px;
    box-shadow: ${theme.colors.shadowSm};
    background: ${theme.colors.paper};
    margin-bottom: 24px;
    display: flex;
    flex-direction: column;
`;

const StyledTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-family: inherit;
    table-layout: fixed; /* Ensures column widths are respected */
`;

const TableHead = styled.thead`
    background-color: ${HEADER_BG};
`;

const TableHeaderCell = styled.th`
    padding: 16px;
    text-align: ${({ align }) => align || 'left'};
    font-size: 14px;
    font-weight: 600;
    color: ${theme.colors.headerText}; /* White text on dark background */
    border-bottom: 1px solid ${theme.colors.border};
    width: ${({ width }) => width || 'auto'};
    cursor: ${({ sortable }) => (sortable ? 'pointer' : 'default')};
    user-select: none;
    white-space: nowrap;

    &:hover {
        background-color: ${({ sortable }) => (sortable ? 'rgba(255, 255, 255, 0.1)' : 'transparent')};
    }
`;

const TableRow = styled.tr`
    background-color: ${theme.colors.paper};
    transition: background-color 0.2s ease;
    height: 57px; /* Fixed height for consistency */

    &:nth-child(even) {
        background-color: #f8fafc;
    }

    &:hover {
        background-color: rgba(0, 194, 224, 0.04);
    }
`;

const EmptyRow = styled.tr`
    height: 57px;
    background-color: transparent;
`;

const TableCell = styled.td`
    padding: 0 16px; /* Vertical padding handled by row height */
    font-size: 14px;
    color: ${theme.colors.text};
    text-align: ${({ align }) => align || 'left'};
    border-bottom: 1px solid ${theme.colors.border};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const EmptyState = styled.div`
    padding: 48px;
    text-align: center;
    color: ${theme.colors.textSecondary};
    font-style: italic;
    background: ${theme.colors.paper};
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const PaginationContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-top: 1px solid ${theme.colors.border};
    background-color: ${theme.colors.paper};
`;

const PaginationControls = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
`;

const PageButton = styled.button`
    padding: 6px 12px;
    border-radius: 6px;
    border: 1px solid ${theme.colors.border};
    background: white;
    font-size: 13px;
    cursor: pointer;
    color: ${theme.colors.text};

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }

    &:hover:not(:disabled) {
        background: #f1f5f9;
    }
`;

const RowsPerPageSelect = styled.select`
    padding: 6px 8px;
    border-radius: 6px;
    border: 1px solid ${theme.colors.border};
    background: white;
    font-size: 13px;
    color: ${theme.colors.text};
    cursor: pointer;
    outline: none;

    &:focus {
        border-color: ${theme.colors.action};
    }
`;

const compareValues = (a, b, order) => {
    const valueA = a == null ? '' : String(a).toLowerCase();
    const valueB = b == null ? '' : String(b).toLowerCase();

    if (valueA < valueB) return order === 'asc' ? -1 : 1;
    if (valueA > valueB) return order === 'asc' ? 1 : -1;
    return 0;
};

const Table = ({
    columns,
    rows,
    rowKey = 'id',
    defaultRowsPerPage = 10,
    rowsPerPageOptions = [5, 10, 25, 50],
    emptyMessage = 'Nenhum registro encontrado',
    defaultSortField = null,
    defaultSortOrder = 'asc',
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
    const [sortField, setSortField] = useState(defaultSortField);
    const [sortOrder, setSortOrder] = useState(defaultSortOrder);

    const handleSort = (column) => {
        if (!column.sortable) return;

        if (sortField === column.key) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(column.key);
            setSortOrder('asc');
        }
    };

    const handleRowsPerPageChange = (e) => {
        setRowsPerPage(Number(e.target.value));
        setPage(0);
    };

    const sortedRows = useMemo(() => {
        if (!sortField) return rows;

        const column = columns.find((c) => c.key === sortField);
        if (!column) return rows;

        const getter = column.sortValue || ((row) => row[sortField]);
        return [...rows].sort((a, b) => compareValues(getter(a), getter(b), sortOrder));
    }, [rows, sortField, sortOrder, columns]);

    const paginatedRows = useMemo(() => {
        const start = page * rowsPerPage;
        return sortedRows.slice(start, start + rowsPerPage);
    }, [sortedRows, page, rowsPerPage]);

    // Fill empty rows to maintain height
    const emptyRows = rowsPerPage - paginatedRows.length;

    const totalPages = Math.ceil(sortedRows.length / rowsPerPage);

    return (
        <TableContainer>
            <div style={{ overflowX: 'auto' }}>
                <StyledTable>
                    <TableHead>
                        <TableRow style={{ height: 'auto', backgroundColor: 'transparent' }}>
                            {columns.map((column) => (
                                <TableHeaderCell
                                    key={column.key}
                                    width={column.width}
                                    align={column.align}
                                    sortable={column.sortable}
                                    onClick={() => handleSort(column)}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: column.align === 'right' ? 'flex-end' : 'flex-start' }}>
                                        {column.label}
                                        {column.sortable && sortField === column.key && (
                                            sortOrder === 'asc' ?
                                                <ArrowUpward style={{ fontSize: 16 }} /> :
                                                <ArrowDownward style={{ fontSize: 16 }} />
                                        )}
                                    </div>
                                </TableHeaderCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <tbody>
                        {paginatedRows.length > 0 ? (
                            paginatedRows.map((row, index) => (
                                <TableRow key={typeof rowKey === 'function' ? rowKey(row) : row[rowKey] || index}>
                                    {columns.map((column) => (
                                        <TableCell key={`${column.key}-${index}`} align={column.align}>
                                            {column.render ? column.render(row, index) : row[column.key]}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={columns.length}>
                                    <EmptyState>{emptyMessage}</EmptyState>
                                </td>
                            </tr>
                        )}
                        {/* Render empty rows to maintain fixed height if we have data but not enough to fill page */}
                        {paginatedRows.length > 0 && emptyRows > 0 && Array.from({ length: emptyRows }).map((_, i) => (
                            <EmptyRow key={`empty-${i}`}>
                                <TableCell colSpan={columns.length} />
                            </EmptyRow>
                        ))}
                    </tbody>
                </StyledTable>
            </div>

            <PaginationContainer>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: theme.colors.textSecondary }}>
                    <span>Itens por página:</span>
                    <RowsPerPageSelect value={rowsPerPage} onChange={handleRowsPerPageChange}>
                        {rowsPerPageOptions.map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </RowsPerPageSelect>
                </div>

                <PaginationControls>
                    <span style={{ fontSize: 13, color: theme.colors.textSecondary }}>
                        {rows.length > 0 ? (
                            `${page * rowsPerPage + 1} - ${Math.min((page + 1) * rowsPerPage, rows.length)} de ${rows.length}`
                        ) : (
                            '0 de 0'
                        )}
                    </span>
                    <div style={{ display: 'flex', gap: 4 }}>
                        <PageButton
                            onClick={() => setPage((p) => Math.max(0, p - 1))}
                            disabled={page === 0}
                        >
                            Anterior
                        </PageButton>
                        <PageButton
                            onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                            disabled={page >= totalPages - 1}
                        >
                            Próxima
                        </PageButton>
                    </div>
                </PaginationControls>
            </PaginationContainer>
        </TableContainer>
    );
};

Table.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string.isRequired,
        label: PropTypes.node.isRequired,
        sortable: PropTypes.bool,
        align: PropTypes.string,
        width: PropTypes.string,
        render: PropTypes.func,
        sortValue: PropTypes.func,
    })).isRequired,
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    rowKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
    defaultRowsPerPage: PropTypes.number,
    rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    emptyMessage: PropTypes.string,
    defaultSortField: PropTypes.string,
    defaultSortOrder: PropTypes.oneOf(['asc', 'desc']),
};

export default Table;
